import classNames from 'classnames';
import { getIn, connect } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';

import CustomFund from 'hb-react/shared/apps/Donations/components/AmountsAndFundsSections/CustomFund';
import OtherAmount from 'hb-react/shared/apps/Donations/components/AmountsAndFundsSections/OtherAmount';
import { getTotalGiftsAmount } from 'hb-react/shared/apps/Donations/utils/gifts';
import { numberWithTwoDecimals } from 'hb-react/shared/utils/formatNumber';

import ErrorMessage from '../ErrorMessage';

const GiftsList = ({
  formik,
  campaign,
  onRemoveGift,
  onChangeAmount,
  onSetAmountsEqually,
  onUpdateGlobalAmountFromGifts,
}) => {
  const gifts = getIn(formik.values, 'donation_order[gifts_attributes]');
  const globalAmount = getIn(formik.values, 'donation_order[amount]');
  const giftsErrors = getIn(formik.errors, 'donation_order[gifts_attributes]');
  const totalGiftsAmount = getTotalGiftsAmount(gifts);

  if (gifts.length < 1) {
    return null;
  }

  return (
    <Box
      display="block"
      bg="smoke"
      p="18px"
      my={4}
      css="border-radius: 6px;"
      data-testid="gifts-list"
    >
      <div className="push--bottom flex flex-center flex-wrap space-between">
        <Box display="block" as="h2" font="h3-regular" lineHeight="41px">
          {__('Selected fund(s)')}
        </Box>
        {gifts.length > 1 && (
          <a
            onClick={() =>
              onSetAmountsEqually({
                giftCount: gifts.length,
                totalAmount: globalAmount,
              })
            }
            className="weight--bold custom-color-links custom-color-button-primary"
          >
            {__('Split equally')}
          </a>
        )}
      </div>

      {gifts.map((gift, index) => {
        const giftAmountError = getIn(giftsErrors, `[${index}][amount]`);

        return (
          <GiftWrapper
            className="push-half--bottom palm--soft--bottom"
            key={gift.title}
          >
            <div className="flex flex-center palm--push-quarter--bottom">
              <i
                className="icon-multiply soft-quarter push-half--right cursor-pointer"
                title={__('Remove the fund from the list')}
                onClick={() => onRemoveGift(index)}
              />
              <Box font="body-semibold-asphalt">{gift.title}</Box>
              <CustomFund
                name={`donation_order[gifts_attributes][${index}][custom_fund]`}
                gift={gift}
              />
            </div>
            <OtherAmountWrapper
              className={classNames('text--right', {
                field_with_errors: giftAmountError,
              })}
            >
              <OtherAmount
                campaign={campaign}
                name="gift_amount"
                otherAmount={gift.amount}
                onChange={(value) =>
                  onChangeAmount({
                    amount: value,
                    giftId: index,
                  })
                }
              />
              {giftAmountError && (
                <AdditionalInformation id={`gift_amount-${index}`} type="error">
                  {giftAmountError}
                </AdditionalInformation>
              )}
            </OtherAmountWrapper>
          </GiftWrapper>
        );
      })}

      <div className="palm--hidden bordered--top border-width--2" />

      <div className="flex flex-center space-between push--top f-size--1-2">
        <Box display="block" font="body-semibold-asphalt">
          {__('Total amount')}
        </Box>
        <Box
          display="block"
          font="body-semibold-asphalt"
          mr={{ _: 3, m: '27px' }}
        >{`${campaign.currency.symbol} ${numberWithTwoDecimals(
          totalGiftsAmount,
        )}`}</Box>
      </div>
      <ErrorMessage
        onSetAmountsEqually={() =>
          onSetAmountsEqually({
            giftCount: gifts.length,
            totalAmount: globalAmount,
          })
        }
        onUpdateGlobalAmountFromGifts={() =>
          onUpdateGlobalAmountFromGifts(totalGiftsAmount)
        }
      />
      <div className="flex flex--justify-center push-half--top">
        <Box display="block" font="metadata-regular-dove" fontStyle="italic">
          {s__('Donations|Amount is in %{currency}').i__({
            currency: campaign.currency.code,
          })}
        </Box>
      </div>
    </Box>
  );
};

GiftsList.propTypes = {
  campaign: PropTypes.object.isRequired,
  onRemoveGift: PropTypes.func.isRequired,
  onSetAmountsEqually: PropTypes.func.isRequired,
  onUpdateGlobalAmountFromGifts: PropTypes.func.isRequired,
  onChangeAmount: PropTypes.func.isRequired,
};

export default connect(GiftsList);

const GiftWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  @include media-query(palm) {
    display: block;
    border-bottom: 1px solid ${({ theme }) => theme.colors.horizon};
  }
`;

const OtherAmountWrapper = styled(Box)`
  display: block;
  input {
    width: 120px;
  }
`;
